import React, { useEffect, useRef, useState } from "react";
import useTranslate from "../../hooks/useTranslate";

// The "…" overflow menu (QAL-T6). Design frames:
//   * 1m·1 — author menu on an own post: "Edit post" / "Delete post",
//     open-state trigger bg #F1F4F8 + text #111827, menu absolute right-0,
//     min-width 160, item 8px 12px / 13px / radius 7 / hover #F5F7FA,
//     panel border #E4E9F0, radius 10, shadow 0 8px 24px rgba(17,24,39,0.10),
//     padding 4px.
//   * 1c Z.408-410 — foreign menu (comment): ONLY "Report", min-width 150.
//   * Trigger at rest: color #9CA3AF, 16px (posts) / 15px (comments),
//     padding 2px 6px, radius 8, hover bg #F1F4F8 + text #111827 (1c Z.362).
//   * Mobile (1k·3): same glyph with a bigger hit area (8px 10px drawn);
//     here the padding grows via negative margin so the drawn row height
//     never shifts.
//
// `own` decides the items: own content → Edit + Delete, foreign → Report
// (exactly one path — the design never mixes them). `type` only changes the
// labels ("post" vs "comment" — comment labels are the workorder-sanctioned
// variant of the drawn post labels).
const LoungeKebabMenu = ({
  own,
  type = "post",
  onEdit,
  onDelete,
  onReport,
  size = "post",
}) => {
  const { t } = useTranslate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Close on outside click / Escape — same approach as the composer's
  // desktop channel dropdown.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (fn) => () => {
    setOpen(false);
    if (fn) fn();
  };

  const isPost = type === "post";
  const item =
    "block w-full text-left py-2 px-3 text-[13px] text-[#111827] rounded-[7px] cursor-pointer border-none bg-transparent hover:bg-[#F5F7FA] whitespace-nowrap";

  return (
    <span ref={rootRef} className="relative inline-flex shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={isPost ? t("Post options") : t("Comment options")}
        data-testid="kebab-trigger"
        className={`border-none cursor-pointer rounded-lg leading-none p-2 -m-1.5 sm:m-0 sm:py-0.5 sm:px-1.5 ${
          size === "post" ? "text-[16px]" : "text-[15px]"
        } ${
          open
            ? "bg-[#F1F4F8] text-[#111827]"
            : "bg-transparent text-[#9CA3AF] hover:bg-[#F1F4F8] hover:text-[#111827]"
        }`}
      >
        …
      </button>
      {open && (
        <div
          role="menu"
          data-testid="kebab-menu"
          className={`absolute top-[26px] sm:top-7 right-0 bg-white border border-[#E4E9F0] rounded-[10px] shadow-[0_8px_24px_rgba(17,24,39,0.10)] p-1 z-20 ${
            size === "post" ? "min-w-[160px]" : "min-w-[150px]"
          }`}
        >
          {own ? (
            <>
              <button type="button" role="menuitem" className={item} onClick={pick(onEdit)}>
                {isPost ? t("Edit post") : t("Edit comment")}
              </button>
              <button type="button" role="menuitem" className={item} onClick={pick(onDelete)}>
                {isPost ? t("Delete post") : t("Delete comment")}
              </button>
            </>
          ) : (
            <button type="button" role="menuitem" className={item} onClick={pick(onReport)}>
              {t("Report")}
            </button>
          )}
        </div>
      )}
    </span>
  );
};

export default LoungeKebabMenu;
